/**
 * lrp.js — typed-cell Long Range Plan API wrappers (Phase 117.A).
 */

import { api } from "./api";

export const getLrpYear = (school_year) =>
  api.get("/lrp/year", { params: { school_year } }).then((r) => r.data);

export const putLrpCell = ({ school_year, subject_key, month_key, text }) =>
  api
    .put("/lrp/cell", { school_year, subject_key, month_key, text })
    .then((r) => r.data);

export const listLrpYears = () =>
  api.get("/lrp/years").then((r) => r.data?.years || []);

export const copyLrpFromYear = ({ from_school_year, to_school_year }) =>
  api
    .post("/lrp/copy-from-year", { from_school_year, to_school_year })
    .then((r) => r.data);

export const exportLrpPdfUrl = (school_year) =>
  `/lrp/export.pdf?school_year=${encodeURIComponent(school_year)}`;

/**
 * Compute the current Ontario school year string. Ontario school
 * years span September → June; the cutover happens August 1 by
 * convention so an August "fresh start" actually opens the new year.
 */
export const currentSchoolYear = (now = new Date()) => {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed
  // Aug (7) onward → year starts now; otherwise we're in the spring half of last year's plan
  if (m >= 7) return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  return `${y - 1}-${String(y % 100).padStart(2, "0")}`;
};

/**
 * The school year whose September the teacher is planning ahead for.
 * Used by the summer Today-card "plan ahead for September" CTA so the
 * teacher lands on the *upcoming* year in July (before the Aug 1
 * rollover) without disturbing `currentSchoolYear`'s "before Aug 1,
 * unchanged" contract for direct visits to /long-range-plan.
 *
 * In July or August of calendar year Y, the next September belongs to
 * school year Y–(Y+1). Elsewhere we defer to `currentSchoolYear` so a
 * defensive caller outside summer doesn't get a surprise value.
 */
export const upcomingSeptemberSchoolYear = (now = new Date()) => {
  const m = now.getMonth();
  if (m === 6 || m === 7) {
    const y = now.getFullYear();
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  }
  return currentSchoolYear(now);
};

/**
 * Validate a raw `?school_year=` querystring value. Returns the
 * canonicalised "YYYY-YY" string when the input is well-formed and
 * inside the accepted rolling window, or `null` otherwise. Malformed,
 * out-of-range, and internally-inconsistent inputs return `null` — the
 * caller falls back to `currentSchoolYear()`. No throws, no console
 * noise: this is a URL a teacher may have mangled by accident.
 *
 * Accepted window (founder decision, Jul 11 2026): from five school
 * years before the current one, up to one year forward — that
 * covers a teacher's realistic archive of prior plans, plus the
 * "plan ahead for September" case, and nothing beyond.
 */
export const parseSchoolYearParam = (raw, now = new Date()) => {
  if (typeof raw !== "string") return null;
  const m = /^(\d{4})-(\d{2})$/.exec(raw.trim());
  if (!m) return null;
  const startYear = Number(m[1]);
  const suffix = Number(m[2]);
  if (!Number.isFinite(startYear) || !Number.isFinite(suffix)) return null;
  // Internal consistency — the two halves must be consecutive years.
  const expectedSuffix = (startYear + 1) % 100;
  if (suffix !== expectedSuffix) return null;
  // Rolling window relative to the Aug-1 current year.
  const current = currentSchoolYear(now);
  const currentStart = Number(current.slice(0, 4));
  if (!Number.isFinite(currentStart)) return null;
  if (startYear < currentStart - 5 || startYear > currentStart + 1) return null;
  return `${startYear}-${String(suffix).padStart(2, "0")}`;
};
